/**
 * M1 Catalog API client (issues #17–#21; repointed to the rebuilt
 * kitchen context in #1151).
 *
 * Wraps the four `/api/catalog/*` HTTP resources (ingredients, dishes,
 * combos, suppliers) with typed helpers for the frontend
 * `ResourceDefinition` plumbing. List calls return the locked `/api`
 * list envelope (`ListPage<T>`, see `list-envelope.ts`), which each resource
 * schema renames to the shared surface's `ListResult`. Detail / create /
 * update return the unwrapped record (`http.get` strips the outer `{ data }`),
 * which is what that surface consumes directly since #2167 PR-2.
 * Sub-resource endpoints (`/dishes/:id/recipe`,
 * `/combos/:id/dishes`) fan out into custom editors (#23) and return
 * the inner `{ lines }` / `{ slots }` object after the outer unwrap.
 *
 * NOTE: the archived backend exposed per-supplier price rows under
 * `/catalog/suppliers/:id/prices`. The rebuilt backend models these as a
 * first-class `/api/catalog/supplier-ingredients` resource, and no live
 * UI consumed the old price helpers (only the service test did), so they
 * were dropped here rather than repointed. A supplier-ingredient editor
 * that needs them can add typed helpers against the new resource then.
 */
import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import type { components } from '@/types/api.generated';
import type { FilterWhitelist } from '@resource-kit/controllers';
import { toApiListParams, type ListRequestParams } from './list-request';
import type { CanonicalUnit, ComboSlot } from './catalog-enums';

// ─────────────────────────────────────────────────────────────────
// Row types = generated OpenAPI components (ADR-0006 route-is-spec;
// #1405 type-chain closure) — the exported names stay the historical
// `Supplier` / `Combo` / `Ingredient` / `Dish` so callers are
// untouched, but a backend contract change now fails `tsc` here.
// `DishIngredient` / `ComboDishSlot` stay hand-written: the generated
// contract exposes them only as anonymous inline schemas on
// `CatalogRecipe.lines[]` / `CatalogComboDishes.slots[]` (single-source
// gap, no named component to alias).
// ─────────────────────────────────────────────────────────────────

export type Ingredient = components['schemas']['CatalogIngredient'];
export type Supplier = components['schemas']['CatalogSupplier'];
export type Dish = components['schemas']['CatalogDish'];

/**
 * Recipe line as returned by `GET /api/catalog/dishes/:id/recipe`
 * (`CatalogRecipe.lines[]`). The read wire carries the row `id` but not
 * the parent `dish_id` (it is implied by the path), so `dish_id` is
 * absent here — the editor keys on `ingredient_id`.
 */
export interface DishIngredient {
  id: string;
  dish_id?: string;
  ingredient_id: string;
  qty_per_portion: string;
  // Closed-set per backend `dish_ingredients.unit` column.
  unit: CanonicalUnit;
  prep_note?: string | null;
}

export type Combo = components['schemas']['CatalogCombo'];

/**
 * Combo slot as returned by `GET /api/catalog/combos/:id/dishes`
 * (`CatalogComboDishes.slots[]`). The read wire omits the parent
 * `combo_id` (implied by the path).
 */
export interface ComboDishSlot {
  id: string;
  combo_id?: string;
  dish_id: string;
  // Closed-set per backend `combo_dishes.slot` route-boundary zod (#80).
  slot: ComboSlot;
  display_order: number;
}

// ─────────────────────────────────────────────────────────────────
// API clients
// ─────────────────────────────────────────────────────────────────

// The catalog list routes accept `search` / `page` / `size` (and
// `status` on dishes + combos). `toApiListParams` maps the shared
// `limit` → the kernel `size` key; a `status` filter passed as a bare
// `extraParams` value is whitelisted through as a top-level query param.
const NO_FILTERS: FilterWhitelist = {};
const STATUS_FILTER: FilterWhitelist = { status: ['eq'] };

export const ingredientAPI = {
  list: (params?: ListRequestParams): Promise<ListPage<Ingredient>> =>
    http.get('/api/catalog/ingredients', { params: toApiListParams(params ?? {}, NO_FILTERS) }),
  detail: (id: string): Promise<Ingredient> => http.get(`/api/catalog/ingredients/${id}`),
  create: (body: Partial<Ingredient>): Promise<Ingredient> =>
    http.post('/api/catalog/ingredients', body),
  update: (id: string, body: Partial<Ingredient>): Promise<Ingredient> =>
    http.patch(`/api/catalog/ingredients/${id}`, body),
  delete: (id: string): Promise<void> => http.delete(`/api/catalog/ingredients/${id}`),
};

export const supplierAPI = {
  list: (params?: ListRequestParams): Promise<ListPage<Supplier>> =>
    http.get('/api/catalog/suppliers', { params: toApiListParams(params ?? {}, NO_FILTERS) }),
  detail: (id: string): Promise<Supplier> => http.get(`/api/catalog/suppliers/${id}`),
  /**
   * Batch reference read behind the read-only many2one link (#1793).
   *
   * Contract, per the route: ids travel in the shared `in:<id>,<id>` filter
   * wire form, at most 200 per request (more is a 400); response ORDER is
   * unspecified, so the caller keys rows by id; ids with no live row — and
   * soft-deleted rows — are simply absent from the answer.
   */
  byIds: (ids: readonly string[]): Promise<Supplier[]> =>
    http.get('/api/catalog/suppliers/by-ids', { params: { id: `in:${ids.join(',')}` } }),
  create: (body: Partial<Supplier>): Promise<Supplier> => http.post('/api/catalog/suppliers', body),
  update: (id: string, body: Partial<Supplier>): Promise<Supplier> =>
    http.patch(`/api/catalog/suppliers/${id}`, body),
  delete: (id: string): Promise<void> => http.delete(`/api/catalog/suppliers/${id}`),
};

export const dishAPI = {
  list: (params?: ListRequestParams): Promise<ListPage<Dish>> =>
    http.get('/api/catalog/dishes', { params: toApiListParams(params ?? {}, STATUS_FILTER) }),
  detail: (id: string): Promise<Dish> => http.get(`/api/catalog/dishes/${id}`),
  create: (body: Partial<Dish>): Promise<Dish> => http.post('/api/catalog/dishes', body),
  update: (id: string, body: Partial<Dish>): Promise<Dish> =>
    http.patch(`/api/catalog/dishes/${id}`, body),
  delete: (id: string): Promise<void> => http.delete(`/api/catalog/dishes/${id}`),
  recipe: (id: string): Promise<{ lines: DishIngredient[] }> =>
    http.get(`/api/catalog/dishes/${id}/recipe`),
  setRecipe: (
    id: string,
    lines: Array<{
      ingredient_id: string;
      qty_per_portion: string;
      // Write path uses the same closed-set as the read path — both
      // sides go through `CanonicalUnit` from the bridge so off-list
      // values fail at the call site instead of at the backend 400.
      unit: CanonicalUnit;
      prep_note?: string;
    }>,
  ): Promise<{ line_count: number }> => http.put(`/api/catalog/dishes/${id}/recipe`, { lines }),
};

export const comboAPI = {
  list: (params?: ListRequestParams): Promise<ListPage<Combo>> =>
    http.get('/api/catalog/combos', { params: toApiListParams(params ?? {}, STATUS_FILTER) }),
  detail: (id: string): Promise<Combo> => http.get(`/api/catalog/combos/${id}`),
  create: (body: Partial<Combo>): Promise<Combo> => http.post('/api/catalog/combos', body),
  update: (id: string, body: Partial<Combo>): Promise<Combo> =>
    http.patch(`/api/catalog/combos/${id}`, body),
  delete: (id: string): Promise<void> => http.delete(`/api/catalog/combos/${id}`),
  slots: (id: string): Promise<{ slots: ComboDishSlot[] }> =>
    http.get(`/api/catalog/combos/${id}/dishes`),
  setSlots: (
    id: string,
    // Write path mirrors the read shape: `slot` is `ComboSlot`, not
    // `string`. Off-list values can no longer compile.
    slots: Array<{ slot: ComboSlot; dish_id: string; display_order: number }>,
  ): Promise<{ slot_count: number }> => http.put(`/api/catalog/combos/${id}/dishes`, { slots }),
};
